import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import {
  DateRange,
  CalendarToday,
} from '@mui/icons-material';

export interface TimeRangeValue {
  startDate?: string;
  endDate?: string;
  preset?: string;
}

interface TimeRangeSelectorProps {
  value: TimeRangeValue;
  onChange: (value: TimeRangeValue) => void;
  loading?: boolean;
}

const TIME_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 3 months' },
  { value: '1y', label: 'Last year' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'custom', label: 'Custom range' },
];

const getPresetDates = (preset: string): { startDate: string; endDate: string } | null => {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  
  switch (preset) {
    case '7d': {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      return { startDate: startDate.toISOString().split('T')[0], endDate };
    }
    case '30d': {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      return { startDate: startDate.toISOString().split('T')[0], endDate };
    }
    case '90d': {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 90);
      return { startDate: startDate.toISOString().split('T')[0], endDate };
    }
    case '1y': {
      const startDate = new Date(today);
      startDate.setFullYear(today.getFullYear() - 1);
      return { startDate: startDate.toISOString().split('T')[0], endDate };
    }
    case 'ytd': {
      const startDate = new Date(today.getFullYear(), 0, 1);
      return { startDate: startDate.toISOString().split('T')[0], endDate };
    }
    default:
      return null;
  }
};

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value,
  onChange,
  loading = false
}) => {
  const [customStartDate, setCustomStartDate] = useState(value.startDate || '');
  const [customEndDate, setCustomEndDate] = useState(value.endDate || '');
  const [dateError, setDateError] = useState<string>('');

  const currentPreset = value.preset || '30d';
  const isCustom = currentPreset === 'custom';

  const handlePresetChange = (event: SelectChangeEvent<string>) => {
    const preset = event.target.value;
    setDateError('');
    
    if (preset === 'custom') {
      onChange({
        preset,
        startDate: customStartDate,
        endDate: customEndDate
      });
    } else if (preset === 'all') {
      // No dates: the backend treats missing bounds as the full history.
      onChange({ preset, startDate: undefined, endDate: undefined });
    } else {
      const dates = getPresetDates(preset);
      if (dates) {
        onChange({
          preset,
          startDate: dates.startDate,
          endDate: dates.endDate
        });
      }
    }
  };

  const validateAndApplyCustomDates = () => {
    setDateError('');
    
    if (!customStartDate || !customEndDate) {
      setDateError('Both start and end dates are required');
      return;
    }
    
    const start = new Date(customStartDate);
    const end = new Date(customEndDate);
    const today = new Date();
    
    if (start > end) {
      setDateError('Start date must be before end date');
      return;
    }
    
    if (end > today) {
      setDateError('End date cannot be in the future');
      return;
    }
    
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365 * 2) { // 2 years max
      setDateError('Date range cannot exceed 2 years');
      return;
    }
    
    onChange({
      preset: 'custom',
      startDate: customStartDate,
      endDate: customEndDate
    });
  };

  const formatDateRange = (): string => {
    if (currentPreset === 'all') return 'All time';
    if (!value.startDate || !value.endDate) return '';
    
    const start = new Date(value.startDate);
    const end = new Date(value.endDate);
    
    const formatOptions: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric',
      year: start.getFullYear() !== end.getFullYear() ? 'numeric' : undefined
    };
    
    return `${start.toLocaleDateString('en-US', formatOptions)} - ${end.toLocaleDateString('en-US', formatOptions)}`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DateRange sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Time Range
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="time-range-select-label">Range</InputLabel>
          <Select
            labelId="time-range-select-label"
            value={currentPreset}
            label="Range"
            onChange={handlePresetChange}
            disabled={loading}
            size="small"
          >
            {TIME_PRESETS.map((preset) => (
              <MenuItem key={preset.value} value={preset.value}>
                {preset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isCustom && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={loading}
              error={!!dateError}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={loading}
              error={!!dateError}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="outlined"
              onClick={validateAndApplyCustomDates}
              disabled={loading}
              startIcon={<CalendarToday />}
              size="small"
              sx={{ minHeight: 40 }}
            >
              Apply
            </Button>
          </Box>
        )}
      </Box>

      {dateError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {dateError}
        </Typography>
      )}

      {!isCustom && (currentPreset === 'all' || (value.startDate && value.endDate)) && (
        <Box sx={{ mt: 2 }}>
          <Chip
            label={formatDateRange()}
            variant="outlined"
            color="primary"
            size="small"
            icon={<DateRange />}
          />
        </Box>
      )}
    </Box>
  );
};

export default TimeRangeSelector;
